import React, { useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';
import { MdSend } from 'react-icons/md';
import { throttle } from 'lodash';
import Form from '~components/forms/form';

import { TextareaControlled } from '~components/forms/textarea';

const MAX_MESSAGE_CHARS = 1000;

const ChatSubmitButton = ({ isDisabled, control }) => {
  const intl = useIntl();
  const watchMessage = useWatch({
    control,
    name: 'Message',
    defaultValue: '',
  });

  return (
    <button
      className="c-btn c-chat__btn"
      type="submit"
      name="submit"
      disabled={isDisabled || !watchMessage}
      aria-label={intl.formatMessage({ id: 'components.chat.send', defaultMessage: 'Send message' })}
    >
      <span className="c-chat-btn--desktop">
        {intl.formatMessage({ id: 'components.chat.send', defaultMessage: 'Send' })}
      </span>
      <span className="c-chat-btn--mobile">
        <MdSend aria-hidden="true" />
      </span>
    </button>
  );
};

const ChatForm = ({
  onSubmit = () => {},
  onType = () => {},
  isDisabled = false,
  isRecipientAccountDeleted = false,
  prefillMessage = '',
  onPrefillConsumed = () => {},
  isRejected = false,
}) => {
  const intl = useIntl();

  const validationSchema = Yup.object().shape({
    Message: Yup.string().required().max(MAX_MESSAGE_CHARS),
  });

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      Message: '',
    },
  });

  // When a conversation starter suggestion is selected, pre-fill the textarea
  useEffect(() => {
    if (prefillMessage) {
      methods.setValue('Message', prefillMessage, { shouldDirty: true });
      if (onPrefillConsumed) {
        onPrefillConsumed();
      }
    }
  }, [prefillMessage, onPrefillConsumed]);

  // Throttled typing indicator triggered on keystroke
  const throttledOnType = useMemo(() => {
    return throttle(
      () => {
        onType();
      },
      2000,
      { leading: true, trailing: false }
    );
  }, [onType]);

  // Clean up throttled typing indicator on unmount
  useEffect(() => {
    return () => {
      throttledOnType.cancel();
    };
  }, [throttledOnType]);

  const handleFormSubmit = () => {
    const formData = methods.getValues();
    if (!formData.Message.trim()) {
      return;
    }

    methods.reset({
      Message: '',
    });
    onSubmit(formData.Message);
  };

  return (
    <Form methods={methods} validationSchema={validationSchema} onSubmit={handleFormSubmit} className="c-chat__form">
      <TextareaControlled
        name="Message"
        isDisabled={isDisabled}
        aria-label={intl.formatMessage({ id: 'components.chat.message-input', defaultMessage: 'Type your message' })}
        placeholder={
          isRecipientAccountDeleted
            ? intl.formatMessage({
                id: 'components.chat.chat-account-deleted',
                defaultMessage: 'This user has deleted the account...',
              })
            : isRejected
            ? intl.formatMessage({
                id: 'components.chat.chat-is-rejected',
                defaultMessage: 'This conversation is closed...',
              })
            : isDisabled
            ? intl.formatMessage({
                id: 'components.chat.chat-is-blocked',
                defaultMessage: 'This conversation is blocked...',
              })
            : intl.formatMessage({ id: 'components.chat.new-message', defaultMessage: 'Type your message...' })
        }
        className="c-input c-chat__input"
        submitOnEnter
        isAutoExpand
        showErrors={false}
        onSubmit={handleFormSubmit}
        onChange={throttledOnType}
      />
      <ChatSubmitButton isDisabled={isDisabled} control={methods.control} />
    </Form>
  );
};

export default ChatForm;
